import {
  call,
  CallEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeEvery,
} from 'redux-saga/effects';

import * as Types from '../actions';
import {
  attachPluginFailure,
  attachPluginSuccess,
  createSessionFailure,
  createSessionSuccess,
  destroySessionFailure,
  destroySessionSuccess,
  detachPluginFailure,
  detachPluginSuccess,
  iceRestart as iceRestartAction,
  iceRestartFailure,
  iceRestartSuccess,
  joinRoomFailure,
  joinRoomSuccess,
  leaveRoomFailure,
  leaveRoomSuccess,
  listRoomParticipantsFailure,
  listRoomParticipantsSuccess,
  toggleMuteAudioFailure,
  toggleMuteAudioSuccess,
  toggleMuteVideoFailure,
  toggleMuteVideoSuccess,
  toggleScreenSharingFailure,
  toggleScreenSharingSuccess,
} from '../actionCreators';
import { StoreState } from '../reducers';

export function* createSession(
  action: Types.CreateSessionRequestAction
): Generator<
  | SelectEffect
  | CallEffect<void>
  | PutEffect<Types.CreateSessionSuccessAction>
  | PutEffect<Types.CreateSessionFailureAction>
> {
  try {
    const { then } = action.payload;
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    yield call(client.createSession);
    const result = createSessionSuccess();
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(createSessionFailure(e.message || e));
  }
}

export function* destroySession(
  action: Types.DestroySessionRequestAction
): Generator<
  | SelectEffect
  | CallEffect<void>
  | PutEffect<Types.DestroySessionSuccessAction>
  | PutEffect<Types.DestroySessionFailureAction>
> {
  const { then, catch: onError } = action.payload;
  try {
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    yield call(client.destroySession);
    const result = destroySessionSuccess();
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(destroySessionFailure(e.message || e));
    if (onError) {
      onError(e);
    }
  }
}

export function* attachConferencePlugin(
  action: Types.ClientAttachPluginRequestAction
): Generator<
  | SelectEffect
  | CallEffect<PluginHandleEmitter>
  | PutEffect<Types.ClientAttachPluginSuccessAction>
  | PutEffect<Types.ClientAttachPluginFailureAction>
> {
  try {
    const { isRemote, userId, then } = action.payload;
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;
    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    const plugin = (yield call(
      client.attachVideoConferencingPlugin,
      isRemote,
      userId
    )) as PluginHandleEmitter;
    const result = attachPluginSuccess({ isRemote, plugin, userId });
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(attachPluginFailure(e.message || e));
  }
}

export function* detachConferencingPlugin(
  action: Types.ClientDetachPluginRequestAction
): Generator<
  | SelectEffect
  | CallEffect<void>
  | PutEffect<Types.ClientDetachPluginSuccessAction>
  | PutEffect<Types.ClientDetachPluginFailureAction>
> {
  const { then, catch: onError } = action.payload;
  try {
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    yield call(client.detachVideoConferencingPlugin);
    const result = detachPluginSuccess();
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(detachPluginFailure(e.message || e));
    if (onError) {
      onError(e);
    }
  }
}

export function* joinRoom(
  action: Types.ClientJoinRequestAction
): Generator<
  | SelectEffect
  | CallEffect<void>
  | PutEffect<Types.ClientJoinSuccessAction>
  | PutEffect<Types.ClientJoinFailureAction>
> {
  try {
    const { roomId, then } = action.payload;
    const { client, user } = (yield select(({ auth, qb }: StoreState) => ({
      client: qb.client,
      user: auth.user,
    }))) as {
      client: QBVideoConferencingClient | undefined;
      user: any;
    };

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    const joinParams: JoinParams = {
      roomId,
      userId: user.id,
      display: user.full_name || user.login,
      video: 'lowres',
    };
    yield call(client.join, joinParams);
    const result = joinRoomSuccess();
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(joinRoomFailure(e.message || e));
  }
}

export function* leaveRoom(
  action: Types.ClientLeaveRequestAction
): Generator<
  | SelectEffect
  | CallEffect<void>
  | PutEffect<Types.ClientLeaveSuccessAction>
  | PutEffect<Types.ClientLeaveFailureAction>
> {
  const { then, catch: onError } = action.payload;
  try {
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    yield call(client.leave);
    const result = leaveRoomSuccess();
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(leaveRoomFailure(e.message || e));
    if (onError) {
      onError(e);
    }
  }
}

export function* listRoomParticipants(
  action: Types.ListRoomParticipantsRequestAction
): Generator<
  | SelectEffect
  | CallEffect<Participant[]>
  | PutEffect<Types.ListRoomParticipantsSuccessAction>
  | PutEffect<Types.ListRoomParticipantsFailureAction>
> {
  try {
    const { then } = action.payload;
    const { client, userId } = (yield select(({ auth, qb }: StoreState) => ({
      client: qb.client,
      userId: auth.user ? auth.user.id : -1,
    }))) as {
      client: QBVideoConferencingClient | undefined;
      userId: number;
    };

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    const roomId = client.currentRoomId;
    if (!roomId) {
      throw new Error('No room Id');
    }

    const participants = (yield call(
      client.listOnlineParticipants,
      roomId
    )) as Participant[];
    const result = listRoomParticipantsSuccess(
      participants.filter((p) => p.id !== userId)
    );
    yield put(result);
    if (then) {
      then(result);
    }
  } catch (e) {
    yield put(listRoomParticipantsFailure(e.message || e));
  }
}

export function* toggleMuteAudio(): Generator<
  | SelectEffect
  | CallEffect<boolean>
  | PutEffect<Types.ToggleMuteAudioSuccessAction>
  | PutEffect<Types.ToggleMuteAudioFailureAction>
> {
  try {
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    const muted = (yield call(client.toggleAudioMute)) as boolean;
    yield put(toggleMuteAudioSuccess(muted));
  } catch (e) {
    yield put(toggleMuteAudioFailure(e.message || e));
  }
}

export function* toggleMuteVideo(): Generator<
  | SelectEffect
  | CallEffect<boolean>
  | PutEffect<Types.ToggleMuteVideoSuccessAction>
  | PutEffect<Types.ToggleMuteVideoFailureAction>
> {
  try {
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    const muted = (yield call(client.toggleVideoMute)) as boolean;
    yield put(toggleMuteVideoSuccess(muted));
  } catch (e) {
    yield put(toggleMuteVideoFailure(e.message || e));
  }
}

export function* toggleScreenSharing(): Generator<
  | SelectEffect
  | CallEffect<boolean>
  | CallEffect<void>
  | PutEffect<Types.ToggleScreenSharingRequestSuccess>
  | PutEffect<Types.ToggleScreenSharingFailureAction>
  | PutEffect<Types.IceRestartRequest>
> {
  const { client, muteVideo, screenshare } = (yield select(
    ({ call, qb }: StoreState) => ({
      client: qb.client,
      muteVideo: call.muteVideo,
      screenshare: call.screenshare,
    })
  )) as {
    client: QBVideoConferencingClient | undefined;
    muteVideo: boolean;
    screenshare: boolean;
  };
  try {
    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    yield call(client.toggleScreenSharing);
    const isSharingEnabled = (yield call(
      client.screenSharingEnabled
    )) as boolean;
    yield put(toggleScreenSharingSuccess(isSharingEnabled));
    if (muteVideo) {
      yield call(toggleMuteVideo);
    }
  } catch (e) {
    if (client) {
      const isSharingEnabled = (yield call(
        client.screenSharingEnabled
      )) as boolean;
      if (isSharingEnabled !== screenshare) {
        yield put(toggleScreenSharingSuccess(isSharingEnabled));
      }
    } else {
      yield put(toggleScreenSharingFailure(e.message || e));
      yield put(iceRestartAction({ isRemote: false }));
    }
  }
}

function* iceRestart(
  action: Types.IceRestartRequest
): Generator<
  | SelectEffect
  | CallEffect<void>
  | PutEffect<Types.IceRestartSuccess>
  | PutEffect<Types.IceRestartFailure>
> {
  try {
    const { userId } = action.payload;
    const client = (yield select(({ qb }: StoreState) => qb.client)) as
      | QBVideoConferencingClient
      | undefined;

    if (!client) {
      throw new Error('No video conferencing plugin instance found');
    }
    yield call(client.iceRestart, userId);
    yield put(iceRestartSuccess());
  } catch (e) {
    yield put(iceRestartFailure(e.message || e));
  }
}

export default [
  takeEvery(Types.CLIENT_CREATE_SESSION_REQUEST, createSession),
  takeEvery(Types.CLIENT_DESTROY_SESSION_REQUEST, destroySession),
  takeEvery(Types.CLIENT_ATTACH_PLUGIN_REQUEST, attachConferencePlugin),
  takeEvery(Types.CLIENT_DETACH_PLUGIN_REQUEST, detachConferencingPlugin),
  takeEvery(Types.CLIENT_JOIN_REQUEST, joinRoom),
  takeEvery(Types.CLIENT_LEAVE_REQUEST, leaveRoom),
  takeEvery(Types.LIST_PARTICIPANTS_REQUEST, listRoomParticipants),
  takeEvery(Types.TOGGLE_MUTE_AUDIO_REQUEST, toggleMuteAudio),
  takeEvery(Types.TOGGLE_MUTE_VIDEO_REQUEST, toggleMuteVideo),
  takeEvery(Types.TOGGLE_SCREENSHARE_REQUEST, toggleScreenSharing),
  takeEvery(Types.ICE_RESTART_REQUEST, iceRestart),
];
